/**
 * FUENTE ÚNICA DE DATOS VERIFICADOS — Qualita Corp SAS
 *
 * Reglas de uso:
 * - Solo información ya publicada y aprobada en el sitio actual.
 * - Ningún dato nuevo se agrega aquí sin validación comercial.
 * - Los datos marcados con `pendingValidation: true` NO deben publicarse
 *   en páginas nuevas ni en structured data hasta ser confirmados.
 *
 * Todo componente, página o schema que necesite una cifra debe leerla
 * desde aquí en lugar de duplicarla.
 */

export const SITE_URL = "https://qualitacorp.com";

/** Identificadores canónicos de entidad (Schema.org @id) */
export const ENTITY_IDS = {
  organization: `${SITE_URL}/#organization`,
  website: `${SITE_URL}/#website`,
  xplora: `${SITE_URL}/#xplora`,
} as const;

export const company = {
  legalName: "Qualita Corp SAS",
  name: "Qualita Corp",
  alternateName: ["Qualita", "Qualita Corp S.A.S."],
  url: SITE_URL,
  logo: `${SITE_URL}/og-image.jpg`,
  /** Descripción de entidad canónica. Mantener consistencia conceptual en todo el sitio. */
  description:
    "Qualita Corp es una empresa RegTech latinoamericana especializada en tecnología para prevención de crímenes financieros, monitoreo de fraude, cumplimiento y gestión de riesgos. Es desarrolladora de la plataforma Xplora.",
  sameAs: ["https://www.linkedin.com/in/qualita-corp-27a24b206"],
  knowsAbout: [
    "RegTech",
    "Prevención de lavado de activos",
    "AML",
    "LA/FT",
    "SARLAFT",
    "SAGRILAFT",
    "Monitoreo transaccional",
    "Prevención de fraude",
    "Screening de listas restrictivas",
    "Gestión de riesgos",
    "Cumplimiento regulatorio",
  ],
  areaServed: "Latinoamérica",
  offices: [
    {
      streetAddress: "Cra 100 #5-169 Torre B, Piso 6, C.C Unicentro",
      addressLocality: "Cali",
      addressCountry: "CO",
      telephone: "+57-315-600-5824",
    },
    {
      streetAddress: "Calle 50, Torre BMW, Piso 10-1026",
      addressLocality: "Ciudad de Panamá",
      addressCountry: "PA",
      telephone: "+507-310-0999",
    },
  ],
  emails: {
    general: "admin@qualitacorp.com",
    dataProtection: "protecciondedatos@qualitacorp.com",
  },
} as const;

export const product = {
  name: "Xplora",
  category: "BusinessApplication",
  subCategory: "RegTech / AML / Fraud Prevention / Risk Management",
  description:
    "Xplora es la plataforma RegTech desarrollada por Qualita Corp para apoyar procesos de cumplimiento, prevención de crímenes financieros, monitoreo de fraude y gestión de riesgos en organizaciones reguladas de Latinoamérica.",
  deploymentModels: ["SaaS", "On-Premise"],
  /** Módulos aprobados comercialmente y ya publicados en el sitio. */
  modules: [
    "aml",
    "fraude",
    "riesgo",
    "find",
    "etica",
    "analytics",
    "integraciones",
  ] as const,
} as const;

/**
 * Métricas ya publicadas en el sitio actual.
 * `context` es obligatorio: nunca mostrar la cifra sin su explicación.
 * `pendingValidation` indica que la cifra requiere confirmación de metodología
 * antes de reutilizarse en páginas o schemas nuevos.
 */
export const metrics = {
  clients: { value: "60+", key: "clients", pendingValidation: false },
  transactions: { value: "30M+", key: "transactions", pendingValidation: false },
  countries: { value: "8", key: "countries", pendingValidation: false },
  detectionPrecision: { value: "98.7%", key: "detectionPrecision", pendingValidation: true },
  falsePositiveReduction: { value: "-73%", key: "falsePositiveReduction", pendingValidation: true },
  opsPerMinute: { value: "2,847", key: "opsPerMinute", pendingValidation: true },
  cloudShare: { value: "98%", key: "cloudShare", pendingValidation: true },
} as const;

/** Métricas seguras para usar en contenido y schema nuevo. */
export const verifiedMetrics = Object.values(metrics).filter((m) => !m.pendingValidation);

/** Integraciones mencionadas en el sitio actual. */
export const integrations = [
  "CRM",
  "Cores transaccionales",
  "Web Services",
  "FTP",
  "Archivos planos",
] as const;

/** Industrias aprobadas. */
export const industries = ["banca", "fintech", "cooperativas"] as const;
